// Commission agents sagas.
//
// Built on the shared watcher factories, so `take` is outside the try in every
// watcher and a failure can never kill one (see makeWatcher.es6.js).
//
// Effect choice:
//   - list, detail and the user typeahead use the takeLatest equivalent, so a
//     fast typist cannot get out-of-order results
//   - writes use the plain watcher; none of them move money
//   - the bulk resolver and the lookup need to read state or pick an endpoint,
//     so they are hand-written — still with `take` outside the try

import { take, call, put, select } from 'redux-saga/effects';
import * as types from '../../lib/actionTypes.es6';
import * as actions from './actions.es6';
import {
  makeWatcher,
  makeLatestWatcher,
  normalizeError
} from '../../components/CommissionsShared/makeWatcher.es6';
import {
  getCommissionAgents,
  getCommissionAgent,
  createCommissionAgent,
  updateCommissionAgent,
  deleteCommissionAgent,
  deleteCommissionAgentPermanent,
  commissionAgentExists,
  getUsers,
  createUser,
  getAgentBanksByAgent,
  createAgentBank,
  updateAgentBank,
  deleteAgentBank,
  deleteAgentBankPermanent
} from '../../lib/requests.es6';

// ─── Reads ───────────────────────────────────────────────────────────────────

export var getCommissionAgentsRequestWatcher = makeLatestWatcher(
  types.GET_COMMISSION_AGENTS_REQUEST,
  getCommissionAgents,
  actions.getCommissionAgentsSuccess,
  actions.getCommissionAgentsFailed
);

export var getCommissionAgentRequestWatcher = makeLatestWatcher(
  types.GET_COMMISSION_AGENT_REQUEST,
  getCommissionAgent,
  actions.getCommissionAgentSuccess,
  actions.getCommissionAgentFailed,
  // Accepts either a bare guid or { guid }.
  { args: function(payload) { return [payload && payload.guid ? payload.guid : payload]; } }
);

// Keycloak search: `first` is an offset, not a page number.
export var getUsersForAgentsRequestWatcher = makeLatestWatcher(
  types.GET_USERS_FOR_AGENTS_REQUEST,
  getUsers,
  actions.getUsersForAgentsSuccess,
  actions.getUsersForAgentsFailed,
  {
    args: function(payload) {
      var request = payload || {};
      var page = request.page || 1;
      var size = request.size || 10;
      return [{
        first: (page - 1) * size,
        max: size,
        search: request.search || ''
      }];
    }
  }
);

// The service caps a list request at 100 (@Max(100) on size).
var RESOLVE_BATCH = 100;

// ─── Bulk agent-name resolution ──────────────────────────────────────────────

// Transaction and ledger tables show agent names, not guids. Rather than one
// request per row, they dispatch the guids on the page and this fetches only
// the ones not already cached — in a single request.
export function* resolveCommissionAgentsWatcher() {
  for (;;) {
    var action = yield take(types.RESOLVE_COMMISSION_AGENTS_REQUEST);
    try {
      var requested = (action.payload && action.payload.guids) || [];
      var cached = yield select(function(state) {
        var slice = state.commissionAgents;
        return (slice && slice.list && slice.list.byGuid) || {};
      });

      var missing = [];
      for (var i = 0; i < requested.length; i += 1) {
        var guid = requested[i];
        if (guid && !cached[guid] && missing.indexOf(guid) === -1) {
          missing.push(guid);
        }
      }
      if (!missing.length) continue;

      // size is capped at 100 by the service (@Max(100) on ListAgentsDTO), so a
      // page showing more than 100 distinct agents would have failed the whole
      // resolution and left every name on it as a raw guid. Fetched in batches
      // instead of one oversized request.
      for (var start = 0; start < missing.length; start += RESOLVE_BATCH) {
        var batch = missing.slice(start, start + RESOLVE_BATCH);
        var response = yield call(getCommissionAgents, {
          agent_guids: batch,
          size: batch.length,
          page: 1
        });
        yield put(actions.resolveCommissionAgentsSuccess(response.data));
      }
    } catch (e) {
      yield put(actions.resolveCommissionAgentsFailed(normalizeError(e)));
    }
  }
}

// ─── Existence check ─────────────────────────────────────────────────────────

// Accepts { guid } or { guids: [...] } and checks each one.
//
// The batch form matters: the user picker needs the answer for every row on the
// page, and this watcher processes one action at a time. Ten separate dispatches
// would see nine SILENTLY DROPPED — actions dispatched while a watcher is
// blocked on `call` are not queued, they are simply never taken. Observed live:
// ten rows, one request, nine rows stuck on "Checking…".
//
// There is no bulk existence endpoint, so the requests are still one per guid;
// they are just issued from inside a single take, where none can be lost.
//
// Each call is individually guarded, so one bad guid does not abandon the rest.
export function* commissionAgentExistsWatcher() {
  for (;;) {
    var action = yield take(types.COMMISSION_AGENT_EXISTS_REQUEST);
    var payload = action.payload || {};
    var guids = payload.guids
      ? payload.guids
      : [payload.guid || payload];

    for (var i = 0; i < guids.length; i += 1) {
      var guid = guids[i];
      if (!guid) continue;
      try {
        var response = yield call(commissionAgentExists, guid);
        yield put(
          actions.commissionAgentExistsSuccess({
            guid: guid,
            response: response ? response.data : null
          })
        );
      } catch (e) {
        var error = normalizeError(e);
        // A 404 is a legitimate "no such agent" answer, not a failure.
        if (error.statusCode === 404) {
          yield put(
            actions.commissionAgentExistsSuccess({
              guid: guid,
              response: false
            })
          );
        } else {
          yield put(actions.commissionAgentExistsFailed(error));
        }
      }
    }
  }
}

// ─── Lookup by the identifiers operators actually know ───────────────────────

var LOOKUP_SEARCH_SIZE = 8;

// Payload: { value }
function lookupAgent(payload) {
  return getCommissionAgents({
    page: 1,
    size: LOOKUP_SEARCH_SIZE,
    search: (payload || {}).value
  });
}

export var lookupCommissionAgentRequestWatcher = makeLatestWatcher(
  types.LOOKUP_COMMISSION_AGENT_REQUEST,
  lookupAgent,
  actions.lookupCommissionAgentSuccess,
  actions.lookupCommissionAgentFailed
);

// ─── Writes ──────────────────────────────────────────────────────────────────

// After a write, refresh the list using the filters the user had applied rather
// than resetting them.
function* refreshAgentList() {
  var params = yield select(function(state) {
    var slice = state.commissionAgents;
    return (slice && slice.listParams) || {};
  });
  yield put(actions.getCommissionAgentsRequest(params));
}

export var createCommissionAgentRequestWatcher = makeWatcher(
  types.CREATE_COMMISSION_AGENT_REQUEST,
  createCommissionAgent,
  actions.createCommissionAgentSuccess,
  actions.createCommissionAgentFailed,
  { onSuccessEffects: refreshAgentList }
);

export var updateCommissionAgentRequestWatcher = makeWatcher(
  types.UPDATE_COMMISSION_AGENT_REQUEST,
  updateCommissionAgent,
  actions.updateCommissionAgentSuccess,
  actions.updateCommissionAgentFailed,
  {
    args: function(payload) { return [payload.guid, payload.body || payload]; },
    onSuccessEffects: refreshAgentList
  }
);

export var deleteCommissionAgentRequestWatcher = makeWatcher(
  types.DELETE_COMMISSION_AGENT_REQUEST,
  deleteCommissionAgent,
  actions.deleteCommissionAgentSuccess,
  actions.deleteCommissionAgentFailed,
  {
    args: function(payload) { return [payload && payload.guid ? payload.guid : payload]; },
    onSuccessEffects: refreshAgentList
  }
);

// Carries the deleted guid on `meta` so the reducer can drop the cached "this
// user is already an agent" answer. A permanent delete frees the user up again,
// and the response body does not say whose record went.
function permanentDeleteSuccess(data, requestPayload) {
  return {
    type: types.DELETE_COMMISSION_AGENT_PERMANENT_SUCCESS,
    payload: data,
    meta: {
      guid:
        requestPayload && requestPayload.guid
          ? requestPayload.guid
          : requestPayload
    }
  };
}

export var deleteCommissionAgentPermanentRequestWatcher = makeWatcher(
  types.DELETE_COMMISSION_AGENT_PERMANENT_REQUEST,
  deleteCommissionAgentPermanent,
  permanentDeleteSuccess,
  actions.deleteCommissionAgentPermanentFailed,
  {
    args: function(payload) { return [payload && payload.guid ? payload.guid : payload]; },
    onSuccessEffects: refreshAgentList
  }
);

// ─── Creating an agent who is not on the platform yet ────────────────────────
//
// Two dependent requests: create the Keycloak user, then create the commission
// agent using the id that user was assigned. The agent's guid IS that user id.
//
// Keycloak's POST /users answers 201 with an EMPTY BODY and the new id only in
// the Location header. That header is not always readable from the browser (it
// needs Access-Control-Expose-Headers), so the id is resolved by falling back to
// a search on the username, which is unique.
export function extractUserIdFromLocation(response) {
  if (!response || !response.headers) return null;
  var location =
    response.headers.location || response.headers.Location || null;
  if (!location) return null;
  var parts = String(location).split('/');
  var last = parts[parts.length - 1];
  return last || null;
}

// Finds the user just created, by username. Returns null rather than throwing so
// the caller can report the partial state precisely.
function* resolveNewUserId(userPayload) {
  var response = yield call(getUsers, {
    search: userPayload.username,
    first: 0,
    max: 20
  });
  var rows = (response && response.data) || [];
  for (var i = 0; i < rows.length; i += 1) {
    if (rows[i] && rows[i].username === userPayload.username) {
      return rows[i].id;
    }
  }
  return null;
}

export function* createAgentWithUserWatcher() {
  for (;;) {
    var action = yield take(types.CREATE_AGENT_WITH_USER_REQUEST);
    var payload = action.payload || {};
    var userPayload = payload.user;
    var agentPayload = payload.agent;
    var userId = null;

    try {
      // Step 1 — the platform user.
      var created = yield call(createUser, userPayload);
      userId = extractUserIdFromLocation(created);
      if (!userId) userId = yield* resolveNewUserId(userPayload);

      if (!userId) {
        // The user exists but cannot be identified, so creating the agent would
        // either fail or invent a guid unconnected to the user.
        yield put(
          actions.createAgentWithUserPartial({
            userCreated: true,
            userId: null,
            username: userPayload.username,
            error: {
              message:
                'The platform user was created, but the service did not return ' +
                'their identifier, so the commission agent was not created. ' +
                'Find the user in User Management and add the agent from there.'
            }
          })
        );
        continue;
      }
    } catch (e) {
      // Nothing was created — an ordinary failure.
      yield put(actions.createAgentWithUserFailed(normalizeError(e)));
      continue;
    }

    try {
      // Step 2 — the commission agent, keyed to that user.
      var agentBody = Object.assign({}, agentPayload, {
        guid: userId,
        user_id: userId,
        aspin_user: true
      });
      var agent = yield call(createCommissionAgent, agentBody);
      yield put(actions.createAgentWithUserSuccess(agent ? agent.data : null));
      yield* refreshAgentList();
    } catch (e) {
      // The user EXISTS now. Saying only "failed" would invite a retry that
      // creates a second user, so the partial state is reported explicitly with
      // the id needed to finish the job.
      var error = normalizeError(e);
      yield put(
        actions.createAgentWithUserPartial({
          userCreated: true,
          userId: userId,
          username: userPayload.username,
          error: {
            message:
              'The platform user was created, but the commission agent was ' +
              'not: ' + error.message + ' The user already exists — do not ' +
              'create them again. Retry adding the agent from the existing ' +
              'user instead.',
            statusCode: error.statusCode
          }
        })
      );
    }
  }
}

// ─── Agent banks (agent-scoped) ──────────────────────────────────────────────

// Carries the agent guid on `meta` so the reducer can key banksByAgent — the
// response body does not say which agent it describes.
function banksSuccess(data, requestPayload) {
  return {
    type: types.GET_AGENT_BANKS_BY_AGENT_SUCCESS,
    payload: data,
    meta: {
      agentGuid:
        requestPayload && requestPayload.agentGuid
          ? requestPayload.agentGuid
          : requestPayload
    }
  };
}

// The service answers 404 for an agent with no bank record at all, so the
// failure has to name the agent too or the builder cannot tell "no banks"
// from "not checked yet".
function banksFailed(error, requestPayload) {
  return {
    type: types.GET_AGENT_BANKS_BY_AGENT_FAILURE,
    payload: error,
    meta: {
      agentGuid:
        requestPayload && requestPayload.agentGuid
          ? requestPayload.agentGuid
          : requestPayload
    }
  };
}

// Deliberately the PLAIN watcher, not the cancelling one: the batch payout
// builder fetches bank details for every selected agent, and a cancelling
// watcher would abandon all but the last request. Sequential is slower but
// correct, and there is no bulk bank endpoint to use instead.
export var getAgentBanksByAgentRequestWatcher = makeWatcher(
  types.GET_AGENT_BANKS_BY_AGENT_REQUEST,
  getAgentBanksByAgent,
  banksSuccess,
  banksFailed,
  {
    args: function(payload) {
      return [payload && payload.agentGuid ? payload.agentGuid : payload];
    }
  }
);

// Bank writes are always scoped to one agent, so each refreshes that agent's
// bank list rather than a global one.
function makeBankRefresh() {
  return function* refreshBanks(data, payload) {
    var agentGuid =
      (payload && (payload.agentGuid || payload.agent_guid)) ||
      (data && data.agent_guid);
    if (agentGuid) {
      yield put(actions.getAgentBanksByAgentRequest({ agentGuid: agentGuid }));
    }
  };
}

export var createAgentBankRequestWatcher = makeWatcher(
  types.CREATE_AGENT_BANK_REQUEST,
  createAgentBank,
  actions.createAgentBankSuccess,
  actions.createAgentBankFailed,
  {
    args: function(payload) { return [payload.body || payload]; },
    onSuccessEffects: makeBankRefresh()
  }
);

export var updateAgentBankRequestWatcher = makeWatcher(
  types.UPDATE_AGENT_BANK_REQUEST,
  updateAgentBank,
  actions.updateAgentBankSuccess,
  actions.updateAgentBankFailed,
  {
    args: function(payload) { return [payload.guid, payload.body || payload]; },
    onSuccessEffects: makeBankRefresh()
  }
);

export var deleteAgentBankRequestWatcher = makeWatcher(
  types.DELETE_AGENT_BANK_REQUEST,
  deleteAgentBank,
  actions.deleteAgentBankSuccess,
  actions.deleteAgentBankFailed,
  {
    args: function(payload) { return [payload.guid]; },
    onSuccessEffects: makeBankRefresh()
  }
);

export var deleteAgentBankPermanentRequestWatcher = makeWatcher(
  types.DELETE_AGENT_BANK_PERMANENT_REQUEST,
  deleteAgentBankPermanent,
  actions.deleteAgentBankPermanentSuccess,
  actions.deleteAgentBankPermanentFailed,
  {
    args: function(payload) { return [payload.guid]; },
    onSuccessEffects: makeBankRefresh()
  }
);
